import { Grid } from '../common/Grid';
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Card, CardContent, Typography, Chip, CircularProgress, Alert, Stack, IconButton, Collapse, Divider, Table, TableHead, TableBody, TableRow, TableCell, Paper, TextField, Select, MenuItem, FormControl, InputLabel, OutlinedInput, Checkbox, ListItemText } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { format, parseISO, isValid } from 'date-fns';
import {
  formatReasonText,
  expandReasonCodes,
  formatTimelineDescription,
} from '../../utils/formatters';
import { formatActorDisplay } from './actorDisplay';
import {
  formatStageName,
  getTimelineEndpoints,
  type TimelineEndpoint,
} from './timelineStageEndpoints';
import orderService from '../../services/orderService';
import api from '../../services/api';
import { headerStripSurface, panelNeutral, surfacePaper } from '../../theme/gridSurfaces';
import { useQuery } from '@tanstack/react-query';
import { verificationPhotoApi, type VerificationPhoto } from '../../services/verificationPhotoService';
import { VerificationPhotoThumbnails } from '../product-verification/VerificationPhotoThumbnails';

/** PRX-51: one frozen empty list, so "no photos" keeps a stable identity across renders. */
const EMPTY_VERIFICATION_PHOTOS: VerificationPhoto[] = [];

// Safe date parser that handles both ISO strings and Date objects
const safeParseDate = (timestamp: string | Date): Date => {
  if (timestamp instanceof Date) return timestamp;
  try {
    const parsed = parseISO(timestamp);
    if (isValid(parsed)) return parsed;
  } catch (e) {
    // parseISO failed, try new Date()
  }
  return new Date(timestamp);
};

interface OrderTimelineViewProps {
  pharmacyId: string;
  orderId: string;
  billingItemId?: string; // Optional: if provided, will fetch and display subscription history events
}

// Category colors matching backend
const CATEGORY_COLORS: Record<string, string> = {
  stage: '#4caf50',
  user_action: '#2196f3',
  system: '#9e9e9e',
  communication: '#9c27b0',
  integration: '#ef6c00',
  exception: '#f44336',
  payment: '#ffc107',
  subscription: '#00bcd4', // Cyan for subscription events
};

const CATEGORY_LABELS: Record<string, string> = {
  stage: 'Stage',
  user_action: 'User Action',
  system: 'System',
  communication: 'Communication',
  integration: 'Integration',
  exception: 'Exception',
  payment: 'Payment',
  subscription: 'Subscription',
};

/**
 * GP-1430: one endpoint of the "Source → Destination" column. A slot that has
 * nothing recorded renders an em dash — the column always shows BOTH sides, so a
 * lone chip can no longer be misread as a completed transition.
 */
const EndpointCell: React.FC<{ endpoint: TimelineEndpoint; side: 'source' | 'destination' }> = ({
  endpoint,
  side,
}) => {
  if (endpoint.kind === 'none') {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid={`timeline-${side}-none`}
        title="Not recorded"
      >
        —
      </Typography>
    );
  }

  if (endpoint.kind === 'missing') {
    // GP-1334: a per-drug stage move with no source stage used to render as a lone
    // destination chip, which read as a normal forward step — name the gap instead.
    //
    // Deliberately NEUTRAL, not a warning: a drug's FIRST stamp (order creation, a
    // newly added Rx) legitimately has no prior stage, and those rows exist on every
    // order. The display layer cannot tell "first ever" from "prior stage destroyed",
    // so it states what it knows — the source is not recorded — without asserting a
    // fault on every order's opening rows.
    return (
      <Chip
        data-testid="missing-prior-stage-chip"
        label={endpoint.label}
        size="small"
        variant="outlined"
        color="default"
        sx={{ fontSize: '0.7rem', height: 20, borderStyle: 'dashed', fontStyle: 'italic' }}
      />
    );
  }

  if (endpoint.kind === 'origin') {
    return (
      <Typography variant="caption" color="text.secondary" data-testid="timeline-origin">
        {endpoint.label}
      </Typography>
    );
  }

  return (
    <Chip
      data-testid={`timeline-${side}-${endpoint.kind}`}
      label={endpoint.label}
      size="small"
      variant="outlined"
      color={
        endpoint.kind === 'troubleshooting'
          ? 'warning'
          : side === 'destination'
            ? 'primary'
            : 'default'
      }
      sx={{ fontSize: '0.7rem', height: 20 }}
    />
  );
};

// Subscription event type for display
interface SubscriptionHistoryEvent {
  id: string;
  action: string;
  timestamp: string;
  performedBy: string;
  performedByUserId?: string;
  changes?: Record<string, { oldValue: any; newValue: any }>;
  notes?: string;
  metadata?: Record<string, any>;
}

// Format date value for display
const formatDateValue = (value: any): string => {
  if (!value) return 'not set';
  try {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return format(new Date(value + 'T00:00:00'), 'MMM dd, yyyy');
    }
    if (typeof value === 'string' && value.includes('T')) {
      return format(new Date(value), 'MMM dd, yyyy');
    }
    return String(value);
  } catch {
    return String(value);
  }
};

// GP-1190: A "Packaging Completed" action log snapshots the product(s) packaged at
// that moment in `metadata.packagingData` (drug identity + lot + expiry). Each
// packaging event is its own immutable log, so when an Rx is swapped brand→generic
// and repackaged, the ORIGINAL (brand) packaging event still carries the brand's
// lot/expiry while the new event carries the generic's. Surfacing this snapshot in
// the timeline restores the traceability that was previously invisible — the UI only
// rendered the `changes` (packagedAt/packagedBy/workflowStage) and never the lot/expiry.
export const getPackagedProducts = (metadata: any): any[] => {
  const data = metadata?.packagingData;
  if (!Array.isArray(data)) return [];
  // Only rows that actually describe a packaged product (identity or lot/expiry).
  return data.filter(
    (p) => p && (p.lotNumber || p.expiryDate || p.din || p.medicationName || p.rxNumber),
  );
};

/**
 * PRX-51: how each item was checked, off a product_verification_completed
 * event.
 *
 * `evidenceSummary` is the ONLY source. It is written by the backend gate from
 * what the server could prove — live photo rows and the persisted scan
 * bookkeeping — and every photo-mode completion carries it. The per-drug
 * `drugVerifications[].evidence` is the CLIENT's own claim on the same PATCH,
 * so falling back to it would let a payload label an item "scanned" in the
 * audit timeline with nothing behind it. An item with no server-derived
 * evidence is omitted, never rendered as "scanned".
 */
export const getVerificationEvidenceRows = (
  metadata: any,
): Array<{ rxNumber: string; evidence: string }> => {
  const summary = metadata?.evidenceSummary;
  if (!summary || typeof summary !== 'object') return [];
  return Object.entries(summary).map(([rxNumber, evidence]) => ({
    rxNumber: String(rxNumber),
    evidence: String(evidence),
  }));
};

/** Plain words for an evidence kind. No abbreviations on a page a pharmacist reads. */
export const describeEvidenceKind = (evidence: string): string => {
  if (evidence === 'scan') return 'Barcode scan';
  if (evidence === 'photo_item') return 'Photo of this item';
  if (evidence === 'photo_pack') return 'Photo of the whole pack';
  return evidence;
};

export const OrderTimelineView: React.FC<OrderTimelineViewProps> = ({
  pharmacyId,
  orderId,
  billingItemId,
}) => {
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [timeline, setTimeline] = useState<any>(null);
  const [subscriptionEvents, setSubscriptionEvents] = useState<SubscriptionHistoryEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Fetch timeline data
  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await orderService.getOrderTimeline(pharmacyId, orderId, {
          includeSystemEvents: true,
        });
        setTimeline(data);
      } catch (err: any) {
        console.error('Failed to load timeline:', err);
        setError(err.message || 'Failed to load timeline');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimeline();

    // Refresh every 60 seconds
    const interval = setInterval(fetchTimeline, 60000);
    return () => clearInterval(interval);
  }, [pharmacyId, orderId]);

  // Fetch subscription history if billingItemId is provided
  useEffect(() => {
    const fetchSubscriptionHistory = async () => {
      if (!billingItemId || !pharmacyId) {
        setSubscriptionEvents([]);
        return;
      }

      try {
        // GP-1431: this used a bare `fetch('/api/…')`, which resolves against the
        // FRONTEND origin and carries no Authorization header — so on a deployed
        // build it hit the SPA's own catch-all route, got a 200 with `index.html`,
        // and `response.json()` threw. The catch below logged the SyntaxError,
        // which serialises to `{}` — the `WARN: Failed to load subscription
        // history: {}` the reporter saw three times with three "successful"
        // /history requests in the network tab, and a Subscription History card
        // that silently never rendered. Route through the shared api client (same
        // baseURL + auth as every other call on this page).
        const response = await api.get(
          `/api/subscriptions/${pharmacyId}/${billingItemId}/history`,
        );
        // The endpoint returns a bare array; tolerate an envelope too rather than
        // throwing a TypeError into the same silent catch.
        const payload = response?.data;
        const events: SubscriptionHistoryEvent[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
        // Filter to only show relevant subscription events (follow-up changes, etc.)
        const relevantEvents = events.filter((event: SubscriptionHistoryEvent) =>
          ['follow_up_changed', 'delivery_frequency_changed', 'paused', 'resumed', 'cancelled'].includes(event.action)
        );
        setSubscriptionEvents(relevantEvents);
      } catch (err: any) {
        // Still non-fatal (the order timeline above is the primary content), but
        // say WHAT failed instead of printing an empty object.
        console.warn(
          `Failed to load subscription history for billing item ${billingItemId}: ` +
            `${err?.response?.status ? `HTTP ${err.response.status} — ` : ''}${err?.message || 'unknown error'}`,
        );
        setSubscriptionEvents([]);
      }
    };

    fetchSubscriptionHistory();
  }, [pharmacyId, billingItemId]);

  // PRX-51: does ANY completion event on this order actually carry photo
  // evidence? Task 7 writes a PRODUCT_VERIFICATION_COMPLETED row on EVERY
  // completion — scan mode included, with evidenceMode 'scan' or (for a
  // pharmacy whose mode could not be resolved) null. Only `evidenceMode
  // === 'photo'` or a non-empty server-derived evidence summary means there
  // is a photo worth fetching. A scan-mode order issues no photo-list request
  // at all.
  const hasPhotoVerificationEvidence = Boolean(
    timeline?.events?.some((group: any) =>
      (group.events ?? []).some((e: any) => {
        if (e.eventType !== 'product_verification_completed') return false;
        return (
          e.metadata?.evidenceMode === 'photo' ||
          getVerificationEvidenceRows(e.metadata).length > 0
        );
      }),
    ),
  );

  // PRX-51: the captured photos for this order, loaded once for the whole
  // timeline rather than per-event, and keyed the same way the product
  // verification dialog keys its own photo-list query so the caches share.
  const verificationPhotosQuery = useQuery({
    queryKey: ['verification-photos', pharmacyId, orderId],
    queryFn: () => verificationPhotoApi.list(pharmacyId, orderId),
    enabled: Boolean(pharmacyId && orderId && hasPhotoVerificationEvidence),
  });
  const verificationPhotos = verificationPhotosQuery.data ?? EMPTY_VERIFICATION_PHOTOS;

  const toggleEventExpanded = (eventId: string) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  // Extract unique categories and users from all events
  const { uniqueCategories, uniqueUsers } = useMemo(() => {
    if (!timeline?.events) return { uniqueCategories: [], uniqueUsers: [] };

    const categories = new Set<string>();
    const users = new Set<string>();

    timeline.events.forEach((group: any) => {
      group.events.forEach((event: any) => {
        if (event.category) categories.add(event.category);
        if (event.performedByName) users.add(event.performedByName);
      });
    });

    return {
      uniqueCategories: Array.from(categories).sort(),
      uniqueUsers: Array.from(users).sort(),
    };
  }, [timeline]);

  // Filter event groups based on search and selected filters
  const filteredEventGroups = useMemo(() => {
    if (!timeline?.events) return [];

    return timeline.events.map((group: any) => {
      const filteredEvents = group.events.filter((event: any) => {
        // Search filter
        if (searchText) {
          const searchLower = searchText.toLowerCase();
          const matchesSearch =
            event.title?.toLowerCase().includes(searchLower) ||
            event.description?.toLowerCase().includes(searchLower) ||
            // GP-1144: match the human-readable reason too (e.g. "PPN/FOC")
            formatTimelineDescription(event.description).toLowerCase().includes(searchLower) ||
            event.performedByName?.toLowerCase().includes(searchLower) ||
            // GP-1430: search what the Source → Destination column actually shows,
            // both endpoints — so "billing troubleshooting" finds the row that says it.
            (() => {
              const { source, destination } = getTimelineEndpoints(event);
              return `${source.label} ${destination.label}`.toLowerCase().includes(searchLower);
            })();

          if (!matchesSearch) return false;
        }

        // Category filter
        if (selectedCategories.length > 0 && !selectedCategories.includes(event.category)) {
          return false;
        }

        // User filter
        if (selectedUsers.length > 0 && !selectedUsers.includes(event.performedByName)) {
          return false;
        }

        return true;
      });

      return {
        ...group,
        events: filteredEvents,
        eventCount: filteredEvents.length,
      };
    }).filter((group: any) => group.events.length > 0); // Only include groups with events
  }, [timeline, searchText, selectedCategories, selectedUsers]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load order timeline. Please try again.
      </Alert>
    );
  }

  if (!timeline || !timeline.events || timeline.events.length === 0) {
    return (
      <Alert severity="info">
        No timeline events found for this order.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxHeight: 1200, overflowY: 'auto', pr: 1 }}>
      {/* Current Order Status - PROMINENT DISPLAY */}
      <Card sx={{ mb: 2, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={1}>
            {timeline.patientName || 'Unknown Patient'} - Order #{timeline.orderNumber}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>
              <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                Current Stage:
              </Typography>
              <Typography variant="h6" fontWeight="bold" data-testid="timeline-current-stage">
                {formatStageName(timeline.currentStage)}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)', mx: 2 }} />
            <Box>
              <Typography variant="caption" display="block" sx={{ opacity: 0.9 }}>
                Status:
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {(timeline.currentStatus || 'active').toUpperCase()}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip
              label={`${timeline.summary?.totalEvents || 0} Events`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`Duration: ${timeline.summary?.totalDuration || 'N/A'}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${timeline.summary?.uniqueUsers || 0} Users Involved`}
              size="small"
              variant="outlined"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card variant="outlined" sx={{ mb: 2, bgcolor: headerStripSurface }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" mb={2}>
            Filter & Search Events
          </Typography>
          <Grid container spacing={2}>
            {/* Search Input */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search actions, descriptions, users..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>
                      🔍
                    </Typography>
                  ),
                }}
              />
            </Grid>

            {/* Category Filter */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  multiple
                  value={selectedCategories}
                  onChange={(e) => setSelectedCategories(e.target.value as string[])}
                  input={<OutlinedInput label="Filter by Status" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={CATEGORY_LABELS[value] || value}
                          size="small"
                          sx={{ bgcolor: CATEGORY_COLORS[value], color: '#fff', height: 20 }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {uniqueCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      <Checkbox checked={selectedCategories.indexOf(category) > -1} />
                      <ListItemText
                        primary={CATEGORY_LABELS[category] || category}
                        secondary={
                          <Chip
                            label=" "
                            size="small"
                            sx={{ bgcolor: CATEGORY_COLORS[category], width: 40, height: 12 }}
                          />
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* User Filter */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by User</InputLabel>
                <Select
                  multiple
                  value={selectedUsers}
                  onChange={(e) => setSelectedUsers(e.target.value as string[])}
                  input={<OutlinedInput label="Filter by User" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {uniqueUsers.map((user) => (
                    <MenuItem key={user} value={user}>
                      <Checkbox checked={selectedUsers.indexOf(user) > -1} />
                      <ListItemText primary={user} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Results Count */}
            {(searchText || selectedCategories.length > 0 || selectedUsers.length > 0) && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredEventGroups.reduce((sum: number, group: any) => sum + group.eventCount, 0)} of{' '}
                  {timeline.summary?.totalEvents || 0} events
                  {searchText || selectedCategories.length > 0 || selectedUsers.length > 0 ? (
                    <>
                      {' '}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => {
                          setSearchText('');
                          setSelectedCategories([]);
                          setSelectedUsers([]);
                        }}
                      >
                        (Clear all filters)
                      </Typography>
                    </>
                  ) : null}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Timeline Events */}
      {filteredEventGroups?.map((group: any, groupIndex: number) => (
        <Box key={group.date} mb={3}>
          {/* Date Header */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: 'primary.main',
              fontWeight: 600,
            }}
          >
            {group.dateLabel} <Chip label={`${group.eventCount} events`} size="small" sx={{ ml: 1 }} />
          </Typography>

          {/* Events Table */}
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                {/* GP-175: Replaced Stage+Status columns with Source→Destination */}
                <TableRow sx={{ bgcolor: panelNeutral }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Source → Destination</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '32%' }}>Event Details</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '8%', textAlign: 'center' }}>More</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.events.map((event: any) => {
                  const isExpanded = expandedEvents.has(event.id);
                  const categoryColor = CATEGORY_COLORS[event.category] || '#9e9e9e';
                  const endpoints = getTimelineEndpoints(event);

                  return (
                    <React.Fragment key={event.id}>
                      <TableRow
                        sx={{
                          borderLeft: `4px solid ${categoryColor}`,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        {/* Date & Time Column */}
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {format(safeParseDate(event.timestamp), 'MMM dd, yyyy')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(safeParseDate(event.timestamp), 'h:mm:ss a')}
                          </Typography>
                        </TableCell>

                        {/* GP-175: Source → Destination Column.
                            GP-1430: BOTH endpoints, always. A transition row renders
                            source → destination with an em dash for whichever side was
                            never recorded; an event that moves nothing (SMS, Descartes,
                            system note) renders its category badge plus an explicit "no
                            stage change" dash, so it can't be mistaken for half of a
                            transition. Troubleshooting rows name the QUEUE they entered
                            or left ("Billing Troubleshooting"), not just the stage. */}
                        <TableCell
                          data-testid="timeline-source-destination"
                          data-transition={endpoints.isTransition ? 'true' : 'false'}
                        >
                          {endpoints.isTransition ? (
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                              <EndpointCell endpoint={endpoints.source} side="source" />
                              <Typography variant="caption" color="text.secondary">→</Typography>
                              <EndpointCell endpoint={endpoints.destination} side="destination" />
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Chip
                                label={CATEGORY_LABELS[event.category] || event.category}
                                size="small"
                                sx={{ bgcolor: categoryColor, color: '#fff', fontSize: '0.65rem', height: 18 }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                data-testid="timeline-no-stage-change"
                                title="No stage change"
                              >
                                —
                              </Typography>
                            </Stack>
                          )}
                        </TableCell>

                        {/* GP-175: Event Details Column — shows title + structured what-changed */}
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {event.title}
                          </Typography>
                          {event.metadata?.changes ? (
                            <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                              {Object.entries(event.metadata.changes).map(([field, change]: [string, any]) => (
                                <Typography key={field} variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {field}: {change?.before ?? '—'} → {change?.after ?? '—'}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {/* GP-1144: expand raw reason codes (e.g. "ppn" → "PPN/FOC")
                                  so cancellation rows read in plain language.
                                  GP-1334: a lost source stage reads "(not recorded)",
                                  never the raw "(unset)". */}
                              {formatTimelineDescription(event.description)}
                            </Typography>
                          )}
                        </TableCell>

                        {/* User Column - GP-183: Show "PHOX (Automated)" for automated steps.
                            GP-1146: named automation triggers (system:<fn>) surface the exact
                            function that ran instead of an anonymous label.
                            GP-981: for user-initiated actions also surface the source
                            screen/queue (metadata.source) so the log shows WHO did it and WHERE. */}
                        <TableCell>
                          {(() => {
                            const actor = formatActorDisplay(event.performedByName, event.eventType);
                            return (
                              <>
                                <Typography variant="body2">
                                  {actor.automated || actor.unknown ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      component="span"
                                      fontStyle={actor.automated ? 'italic' : undefined}
                                    >
                                      {actor.label}
                                    </Typography>
                                  ) : (
                                    actor.label
                                  )}
                                </Typography>
                                {event.metadata?.source && !actor.automated && !actor.unknown && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    component="div"
                                    sx={{ fontSize: '0.7rem' }}
                                  >
                                    via {event.metadata.source}
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </TableCell>

                        {/* Details Expand Button */}
                        <TableCell align="center">
                          {event.metadata && (
                            <IconButton size="small" onClick={() => toggleEventExpanded(event.id)}>
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* GP-175: Expanded Metadata Row — structured details */}
                      {event.metadata && isExpanded && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ bgcolor: headerStripSurface, p: 2 }}>
                            <Typography variant="caption" color="text.secondary" component="div" mb={1}>
                              <strong>Full Event Details:</strong>
                            </Typography>
                            {/* GP-1190: Packaged product snapshot (drug identity + lot + expiry).
                                Rendered from metadata.packagingData so the ORIGINAL product's
                                lot/expiry stays visible on its packaging event even after an Rx
                                swap + repackage supersedes it with a different drug. */}
                            {(() => {
                              const packagedProducts = getPackagedProducts(event.metadata);
                              if (packagedProducts.length === 0) return null;
                              return (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }} component="div" mb={0.5}>
                                    Packaged product{packagedProducts.length > 1 ? 's' : ''} (as packaged on this event)
                                  </Typography>
                                  <Table size="small" sx={{ maxWidth: 620, bgcolor: 'background.paper', borderRadius: 1 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Drug</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Rx #</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Lot #</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Expiry</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Qty</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {packagedProducts.map((p: any, idx: number) => (
                                        <TableRow key={`${p.rxNumber ?? p.din ?? idx}`}>
                                          <TableCell sx={{ fontSize: '0.72rem' }}>{p.medicationName || p.din || '—'}</TableCell>
                                          <TableCell sx={{ fontSize: '0.72rem' }}>{p.rxNumber ?? '—'}</TableCell>
                                          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 500 }}>{p.lotNumber || '—'}</TableCell>
                                          <TableCell sx={{ fontSize: '0.72rem' }}>{p.expiryDate ? formatDateValue(p.expiryDate) : '—'}</TableCell>
                                          <TableCell sx={{ fontSize: '0.72rem' }}>{p.quantityPackaged ?? '—'}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              );
                            })()}
                            {/* PRX-51: how each item was checked at product verification. The
                                evidence kind is read off this event's own snapshot (never from
                                verificationMethod, which reads 'upc' even for a photo-only item);
                                the photos themselves are fetched live by order id so a
                                since-deleted photo stops appearing here too. */}
                            {(() => {
                              const evidenceRows = getVerificationEvidenceRows(event.metadata);
                              if (evidenceRows.length === 0) return null;
                              return (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }} component="div" mb={0.5}>
                                    Verification evidence
                                  </Typography>
                                  <Table size="small" sx={{ maxWidth: 620, bgcolor: 'background.paper', borderRadius: 1 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Rx #</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Checked by</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {evidenceRows.map((row) => (
                                        <TableRow key={row.rxNumber}>
                                          <TableCell sx={{ fontSize: '0.72rem' }}>{row.rxNumber || '—'}</TableCell>
                                          <TableCell sx={{ fontSize: '0.72rem' }}>{describeEvidenceKind(row.evidence)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                  {verificationPhotos.length > 0 ? (
                                    <VerificationPhotoThumbnails
                                      pharmacyId={pharmacyId}
                                      orderId={orderId}
                                      photos={verificationPhotos}
                                    />
                                  ) : verificationPhotosQuery.isError ? (
                                    // No silent fallback: a 403 for a role without photo
                                    // access (or any other read failure) says so in words
                                    // instead of leaving a blank strip. The evidence table
                                    // above still says how each item was checked.
                                    <Typography variant="caption" color="text.secondary" component="div">
                                      Verification photos are unavailable for this account
                                    </Typography>
                                  ) : null}
                                </Box>
                              );
                            })()}
                            {event.metadata.changes ? (
                              <Table size="small" sx={{ maxWidth: 500, bgcolor: 'white', borderRadius: 1 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Field</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Before</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>After</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(event.metadata.changes).map(([field, change]: [string, any]) => (
                                    <TableRow key={field}>
                                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{field}</TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem', color: 'error.main' }}>{change?.before ?? '—'}</TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem', color: 'success.main', fontWeight: 500 }}>{change?.after ?? '—'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Box
                                component="pre"
                                sx={{
                                  fontSize: '0.75rem',
                                  color: 'text.secondary',
                                  whiteSpace: 'pre-wrap',
                                  p: 1,
                                  bgcolor: surfacePaper,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  maxHeight: 300,
                                  overflow: 'auto',
                                }}
                              >
                                {JSON.stringify(event.metadata, null, 2)}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      ))}

      {/* Subscription Events Section */}
      {subscriptionEvents.length > 0 && (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                sx={{
                  bgcolor: CATEGORY_COLORS.subscription,
                  color: 'white',
                  fontWeight: 'bold',
                }}
                label="Subscription"
              />
              Subscription History
            </Typography>
            <Stack spacing={2}>
              {subscriptionEvents.map((event) => {
                const eventDate = safeParseDate(event.timestamp);

                // Format description based on action type
                let description = '';
                if (event.action === 'follow_up_changed' && event.changes?.nextFollowUpDate) {
                  const change = event.changes.nextFollowUpDate;
                  description = `Follow-up date changed from ${formatDateValue(change.oldValue)} to ${formatDateValue(change.newValue)}`;
                } else if (event.action === 'delivery_frequency_changed' && event.changes?.deliveryFrequency) {
                  const change = event.changes.deliveryFrequency;
                  description = `Delivery frequency changed from "${change.oldValue || 'not set'}" to "${change.newValue}"`;
                } else if (event.action === 'paused') {
                  description = 'Subscription paused';
                } else if (event.action === 'resumed') {
                  description = 'Subscription resumed';
                } else if (event.action === 'cancelled') {
                  description = 'Subscription cancelled';
                } else {
                  description = event.notes || event.action;
                }

                return (
                  <Box
                    key={event.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      p: 1.5,
                      bgcolor: headerStripSurface,
                      borderRadius: 1,
                      borderLeft: `4px solid ${CATEGORY_COLORS.subscription}`,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(eventDate, 'MMM dd, yyyy HH:mm')} by {event.performedBy}
                      </Typography>
                      {event.metadata?.reason && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {/* GP-1144: expand raw reason codes here too so the
                              Subscription History card matches the main rows
                              ("ppn" → "PPN/FOC"). */}
                          Reason: {expandReasonCodes(formatReasonText(event.metadata.reason))}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={event.action.replace(/_/g, ' ')}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Stage Durations Summary */}
      {timeline.stageDurations && timeline.stageDurations.length > 0 && (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Stage Durations
            </Typography>
            <Stack spacing={1}>
              {timeline.stageDurations.map((stage: any) => (
                <Box
                  key={stage.stage}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    {stage.stageLabel}
                  </Typography>
                  <Chip
                    label={stage.durationFormatted || 'N/A'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default OrderTimelineView;
